import { KanbanValueSetOption } from '../../model/kanban.value.set';

/**
 * Kanban column bucketing.
 *
 * A kanban column set must be stable across pages: the operator reads "nothing is In Progress", not
 * "In Progress is missing from this page". So columns come from the FIELD'S value set (status list,
 * enum options, lookup picker) rather than from whatever the current page happens to contain, and a
 * column with zero loaded items still renders.
 *
 * KEY-SHAPE INVARIANT — the value-set option key and the row's embedded value key must agree, or a
 * populated column would render empty beside a duplicate stray column. Both sides run through the same
 * `object -> _id ?? id ?? systemName ?? name` derivation as the board's existing `getGroupKey`. For the
 * Stages board that means the WorkOrderStatus picker option `{ _id: '21a320db-…', name: 'Dispatched' }`
 * and the embedded `workOrders.status` `{ _id: '21a320db-…', systemName: 'Dispatched', sortOrder: 300,
 * systemStatus: 'Open' }` both key on the shared `_id`. Static Enum/Status options carry no `_id`, so
 * they key on their option value, which is what the row stores verbatim.
 */

export const KANBAN_EMPTY_BUCKET_KEY = '__empty__';

export type { KanbanValueSetOption };

export type KanbanBucket<T = any> = {
  key: string;
  name: string;
  color?: string | null;
  items: T[];
  count: number;
  sortOrder: number | null;
  /** True when the bucket came from the field's value set rather than from stray page data. */
  known: boolean;
  data?: any;
  /** Row keys this bucket absorbed (its own key plus any aliases). Known buckets only. */
  claimedKeys?: string[];
};

export type ResolveKanbanBucketsOptions<T = any> = {
  items: T[];
  path: string | null | undefined;
  /** The columns to render, already resolved from the view's definitions, or null when the field is
   *  open (free text, failed lookup) and columns must come from the page's own data. */
  valueSet: KanbanValueSetOption[] | null;
  getKey: (value: any) => string;
  getLabel: (value: any) => string;
  getSortOrder: (value: any) => number | null;
  /** Label for the trailing no-value bucket, e.g. "No Status". */
  emptyLabel?: string;
  /** Resolves a swatch colour for a bucket from its source value. */
  getColor?: (value: any) => string | null;
  /**
   * The view's rule columns, so a row whose value the picker no longer offers can still be placed.
   *
   * A DEACTIVATED status is the case this exists for: it is gone from the value set, so nothing
   * claims its key, but the row still carries `systemStatus` and plainly belongs to that system
   * group. Without this the row falls through to the stray fallback and mints a column of its own
   * beside the rule column it belongs in.
   */
  ruleColumns?: KanbanRuleColumn[] | null;
  /**
   * True when the view DECLARES its columns.
   *
   * A declared column set is exhaustive: a value it does not cover is hidden on purpose, so no stray
   * column may be minted for it. Only default one-per-value mode appends strays, where an in-use
   * value absent from the picker still has to be reachable.
   */
  hasDeclaredColumns?: boolean;
};

/** A view rule column, as the bucketer needs it: which bucket, which attribute, which value. */
export type KanbanRuleColumn = {
  /** Bucket key the matching rows land in — the combined column's key. */
  key: string;
  groupBy: string;
  value: string;
  /** `expand` rules render each member as its own column, so an unknown member cannot be folded in. */
  expand?: boolean;
};

/**
 * Groups items into kanban columns.
 *
 * Order is: the resolved columns in source order, then stray data values alphabetically, then the
 * empty bucket. A stray is a value the page carries that no column claims — it still renders, because
 * a row that exists must be reachable; only values the value set knows and no column covers are
 * dropped, and they are dropped upstream by never becoming columns at all.
 */
export function resolveKanbanBuckets<T = any>(options: ResolveKanbanBucketsOptions<T>): KanbanBucket<T>[] {

  const { items, path, valueSet, getKey, getLabel, getSortOrder, emptyLabel, getColor, ruleColumns, hasDeclaredColumns } = options;
  const rows = items ?? [];

  if (!path) {
    return [{ key: 'all', name: 'All', items: [...rows], count: rows.length, sortOrder: null, known: true }];
  }

  const grouped = groupRowsByKey(rows, path, getKey);
  const known = buildKnownBuckets(valueSet, grouped, getColor);
  absorbUncoveredIntoRules(grouped, known, ruleColumns);

  // A declared column set is exhaustive — a value it does not cover is hidden deliberately, and
  // minting a column for it is what put a deactivated "ONSITE" beside the four rule columns.
  const strays = hasDeclaredColumns ? [] : buildStrayBuckets(grouped, known, getLabel, getSortOrder, getColor);
  const empty = buildEmptyBucket(grouped, emptyLabel);

  return [...known, ...strays, ...(empty ? [empty] : [])];
}

/**
 * Place rows whose value no column claims into the rule bucket their GROUP attribute names.
 *
 * The value set is the tenant's picker, and a picker only offers ACTIVE values. A row holding a
 * deactivated status is still a real row in a real system group: `workOrders.status.systemStatus`
 * says "In Progress" whether or not anyone can still pick that status. Reading the attribute off the
 * row rather than off the option is the only way to reach it.
 *
 * An `expand` rule is skipped: it renders one column per member, so an unknown member has no column
 * to be folded into and keeps the stray path (default mode) or stays hidden (declared columns).
 */
function absorbUncoveredIntoRules<T>(grouped: GroupedRows<T>, known: KanbanBucket<T>[], ruleColumns: KanbanRuleColumn[] | null | undefined): void {

  const combining = (ruleColumns ?? []).filter(rule => !rule.expand);
  if (!combining.length) return;

  const claimed = new Set(known.flatMap(bucket => bucket.claimedKeys ?? [bucket.key]));
  const bucketByKey = new Map(known.map(bucket => [bucket.key, bucket]));

  for (const group of grouped.values()) {
    if (group.key === KANBAN_EMPTY_BUCKET_KEY || claimed.has(group.key)) continue;

    const rule = combining.find(candidate => readRowGroupValue(group.rawValue, candidate.groupBy) === candidate.value);
    const bucket = rule ? bucketByKey.get(rule.key) : null;
    if (!bucket) continue;

    bucket.items = [...bucket.items, ...group.items];
    bucket.count = bucket.items.length;
    // Claiming the key is what makes the LANE projection find these rows too: a lane looks its cells
    // up by the bucket's claimed keys, so an unclaimed key would render the column empty per lane.
    bucket.claimedKeys = [...(bucket.claimedKeys ?? [bucket.key]), group.key];
  }
}

/** Reads a grouping attribute off the row's own embedded value (`status.systemStatus`). */
function readRowGroupValue(rawValue: any, groupBy: string): string | null {

  if (!rawValue || typeof rawValue !== 'object') return null;

  const value = groupBy.split('.').reduce<any>((acc, part) => acc?.[part], rawValue);
  return value == null ? null : String(value);
}

type GroupedRows<T> = Map<string, { key: string; rawValue: any; items: T[] }>;

function groupRowsByKey<T>(rows: T[], path: string, getKey: (value: any) => string): GroupedRows<T> {

  const grouped: GroupedRows<T> = new Map();

  for (const row of rows) {
    const rawValue = readPath(row, path);
    const key = getKey(rawValue);
    const existing = grouped.get(key);

    if (existing) {
      existing.items.push(row);
      continue;
    }

    grouped.set(key, { key, rawValue, items: [row] });
  }

  return grouped;
}

function buildKnownBuckets<T>(valueSet: KanbanValueSetOption[] | null, grouped: GroupedRows<T>, getColor?: (value: any) => string | null): KanbanBucket<T>[] {

  if (!valueSet?.length) return [];

  const seen = new Set<string>();
  const buckets: KanbanBucket<T>[] = [];

  for (const option of valueSet) {
    if (!option?.key || seen.has(option.key)) continue;

    // A bucket claims its own key plus every alias it was shaped with, so one column can stand for a
    // whole group of stored values. Aliases already claimed by an earlier bucket are skipped — the
    // first claimant wins, which keeps a row in exactly one column.
    const claimed: string[] = [];
    for (const key of [option.key, ...(option.keys ?? [])]) {
      if (!key || seen.has(key)) continue;
      seen.add(key);
      claimed.push(key);
    }

    const rows = claimed.flatMap(key => grouped.get(key)?.items ?? []);
    // The option's own colour wins; the resolver is the fallback for sources that carry a system
    // status rather than a stored swatch (WorkOrderStatus, Priority).
    const color = option.color ?? getColor?.(option.data ?? option) ?? null;

    buckets.push({
      key: option.key,
      name: option.name,
      color,
      items: rows,
      count: rows.length,
      sortOrder: option.sortOrder ?? null,
      known: true,
      data: option.data ?? null,
      claimedKeys: claimed,
    });
  }

  return buckets;
}

function buildStrayBuckets<T>(grouped: GroupedRows<T>, known: KanbanBucket<T>[], getLabel: (value: any) => string, getSortOrder: (value: any) => number | null, getColor?: (value: any) => string | null): KanbanBucket<T>[] {

  // Every key a known bucket claimed — its own and its aliases — so a grouped value that landed in a
  // collapsed column is not ALSO emitted as a stray duplicate beside it.
  const knownKeys = new Set(known.flatMap(bucket => bucket.claimedKeys ?? [bucket.key]));
  const strays: KanbanBucket<T>[] = [];

  for (const group of grouped.values()) {
    if (group.key === KANBAN_EMPTY_BUCKET_KEY || knownKeys.has(group.key)) continue;

    strays.push({
      key: group.key,
      name: getLabel(group.rawValue) || group.key,
      color: getColor?.(group.rawValue) ?? null,
      items: [...group.items],
      count: group.items.length,
      sortOrder: getSortOrder(group.rawValue),
      known: false,
      data: group.rawValue,
    });
  }

  return strays.sort((left, right) => compareStrays(left, right));
}

function compareStrays<T>(left: KanbanBucket<T>, right: KanbanBucket<T>): number {

  if (left.sortOrder != null && right.sortOrder != null && left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
  if (left.sortOrder != null && right.sortOrder == null) return -1;
  if (left.sortOrder == null && right.sortOrder != null) return 1;
  return left.name.localeCompare(right.name);
}

function buildEmptyBucket<T>(grouped: GroupedRows<T>, emptyLabel?: string): KanbanBucket<T> | null {

  const group = grouped.get(KANBAN_EMPTY_BUCKET_KEY);
  if (!group) return null;

  return {
    key: KANBAN_EMPTY_BUCKET_KEY,
    name: emptyLabel ?? 'No value',
    color: null,
    items: [...group.items],
    count: group.items.length,
    sortOrder: null,
    known: true,
    data: null,
  };
}

function readPath(item: any, path: string): any {

  if (!item) return null;
  return path.split('.').reduce((value, part) => value?.[part], item);
}
